import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faChevronLeft, faClockRotateLeft, faCopy, faPaperPlane } from '@fortawesome/pro-solid-svg-icons';

import { withRouter, Link } from 'react-router-dom';
import { DropdownMenu } from '@buildoutinc/blueprint-react/ui/DropdownMenu';

import LeadClientReportNotes from './show/client_report_notes';
import LeadInfo from './show/info';
import EditContactLink from './show/edit_contact_link';
import PVPActivity from './show/pvp_activity';
import LoadingSpinner from 'components/ui/loading_spinner';
import { getProject } from '../../selectors';
import NotifyLead from './show/notify_lead';
import ShareLinkByEmailModal from 'components/documents/share_link_by_email_modal';
import InviteActivityModal from 'components/documents/invite_activity_modal';
import LeadReferralSourceSelect from './show/lead_referral_source_select';
import LeadStatusSelect from './show/lead_status_select';
import DocAccessLevelSelect from 'bundles/leads/index/table/cells/doc_access_level';
import DocumentProgress from 'bundles/leads/index/table/cells/document_progress/index';
import {
  DOC_ACCESS_LEVEL_FOR_LEASE,
  DOC_ACCESS_LEVEL_FOR_SALE
} from 'bundles/leads/index/table/cell';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { fetchLeads, removeLead, showUpdateLeadModal } from 'bundles/project/actions/index';
import GatesProgressBar from './show/gates_progress/bar';
import { refreshLead } from '../../actions/leads';
import { Card, Col, Row } from 'react-bootstrap-5';
import { Button } from 'components/ui/button';
import { showSnackbar } from 'components/ui/snackbar/helper';

const SHARE_DOC_MODAL_DESCRIPTION = 'Easily invite your lead to view your locked documents.';
const WITH_GATES =
  ` Once sent, the invitee will adhere to the CA & Access settings you've configured for your documents.`;
const WITHOUT_GATES = ' The invitee will skip the waitlist.';

export function LeadShow({
  lead,
  project,
  isFetchingLeads,
  features,
  fetchLeads,
  removeLead,
  refreshLead,
  showUpdateLeadModal,
  useVaultGates
}) {
  const [showShareByEmailModal, setShowShareByEmailModal] = useState(false);
  const [showInviteActivity, setShowInviteActivity] = useState(false);

  // The lead_invite_activity customization gates only what the broker sees.
  // SendGrid events are recorded for every company regardless, so a company
  // switched on later already has the history behind this UI.
  const showInviteActivityUi = Boolean(features.leadInviteActivity && lead && lead.inviteActivity);

  const openShareByEmailModal = () => setShowShareByEmailModal(true);
  const hideShareByEmailModal = () => {
    setShowShareByEmailModal(false);
    refreshLead(lead);
  };

  useEffect(() => {
    if (!lead && isFetchingLeads) {
      // this only occurs when navigating directly to a lead by URL
      // leads are fetched on mount in bundles/project/components/leads/list, which hasnt mounted yet
      fetchLeads(project.id);
    }
  }, [lead, isFetchingLeads]);

  if (isFetchingLeads) return <LoadingSpinner addBottomBuffer/>;

  if (!lead) {
    return (
      <div className="mt-5">
        Sorry, we couldn't find this lead.
      </div>
    );
  }

  const leadInfo = {
    accountNotVerified: lead.accountStatus === 'Not Verified',
    caNotSigned: !lead.signedCa,
    pendingApproval: lead.approvalStatus && !['approved', 'auto_approved'].includes(lead.approvalStatus), // waitlisted
    profileNotComplete: !(lead.profileId && lead.profileUserType),
    viewDocsPrompt: !lead.documentsViewed,
    waitlistApproved: ['approved', 'auto_approved'].includes(lead.approvalStatus)
  };

  const renderEditableFields = () => {
    return (
      <div className="form-vertical-field-container">
        <div className="form-group" key="lead-pi-status" name="lead-pi-status">
          <label className="form-label">Lead Status</label>
          <LeadStatusSelect lead={lead} />
        </div>
        <div className="form-group" key="lead-pi-referral-source" name="lead-pi-referral-source">
          <label className="form-label">Referral Source</label>
          <LeadReferralSourceSelect lead={lead} />
        </div>
        {project.leaseChecked && lead.listingLabel &&
          <div className="form-group" key="lead-pi-space" name="lead-pi-space">
            <label className="form-label">Space(s)</label>
            <p>{lead.listingLabel}</p>
          </div>
        }
        {renderDocsAccessLevelDropdowns()}
      </div>
    );
  };

  const renderDocsAccessLevelDropdowns = () => {
    const SelectOption = project.useVaultGates ? DocumentProgress : DocAccessLevelSelect;

    const dropdownForSale = (project.saleChecked &&
      <div className="form-group" key="lead-pi-doc-access-level-for-sale" name="lead-pi-doc-access-level-for-sale">
        <label className="form-label">Doc Access Level for Sale</label>
        <SelectOption columnName={DOC_ACCESS_LEVEL_FOR_SALE} lead={lead} />
      </div>
    );

    const dropdownForLease = (project.leaseChecked &&
      <div className="form-group" key="lead-pi-doc-access-level-for-lease" name="lead-pi-doc-access-level-for-lease">
        <label className="form-label">Doc Access Level for Lease</label>
        <SelectOption columnName={DOC_ACCESS_LEVEL_FOR_LEASE} lead={lead} />
      </div>
    );

    return (
      <React.Fragment>
        {dropdownForSale}
        {dropdownForLease}
      </React.Fragment>
    );
  };

  const requestMoreInformationNotesSection = () => {
    if (lead.requestMoreInformationNotes.length === 0) return null;

    return (
      <div className="form-group" key="lead-pi-request-more-info-notes" name="lead-pi-request-more-info-notes">
        <label className="form-control">Request More Information (From Plugin)</label>
        <div className="table-container">
          <table>
            <tbody>
              {lead.requestMoreInformationNotes.map((note, index) => (
                <tr key={index}>
                  <td>{note.createdAt}</td>
                  <td>{note.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const newLeadDetails = () => {
    return (
      <div className="space-above">
        <NotifyLead
          lead={lead}
          leadInfo={leadInfo}
        />
        {lead.propertyVaultProfiles.map(pvp => (
          <GatesProgressBar
            caSigned={!!pvp.caSigned}
            key={`${pvp.id}-${pvp.saleOrLease}`}
            lead={lead}
            leadInfo={leadInfo}
            saleOrLease={pvp.saleOrLease}
            showSaleOrLease={lead.propertyVaultProfiles.length > 1}
            userType={lead.profileUserType}
          />
        ))}
        {renderEditableFields()}
        {features.clientReport && <LeadClientReportNotes className="mt-4" lead={lead} />}
        {requestMoreInformationNotesSection()}
        <PVPActivity lead={lead} />
        <div className="mt-5">
          <Link name="delete-lead" to="/project_leads">
            <Button
              icon={faTrash}
              label="Delete Lead"
              variant="destructive"
              onClick={() => removeLead(project.id, lead)}
            />
          </Link>
        </div>
      </div>
    );
  };

  const inviteButton = (
    <Button icon={faPaperPlane} variant="outline">
      Invite to view
      <FontAwesomeIcon icon={faCaretDown} />
    </Button>
  );

  return (
    <React.Fragment>
      <div className="lead-show-page">
        <div className="mb-2">
          <Link name="lead-show-back-link" to="/project_leads">
            <FontAwesomeIcon className="me-2" icon={faChevronLeft}/>
            Back to Leads
          </Link>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <div className="flex-grow-1"><h5>{lead.displayName}</h5></div>
          <div>
            <DropdownMenu>
              <DropdownMenu.Trigger render={inviteButton}/>
              <DropdownMenu.Content align="start">
                <DropdownMenu.Item onClick={openShareByEmailModal}>
                  <FontAwesomeIcon icon={faPaperPlane}/> Invite to view
                </DropdownMenu.Item>
                {(lead.shareLinks || []).map(({ label, url }) => (
                  <CopyInviteLinkMenuItem
                    key={label}
                    label={lead.shareLinks.length > 1 ? `Copy ${label} invite link` : 'Copy invite link'}
                    url={url}
                  />
                ))}
                {showInviteActivityUi ? (
                  <DropdownMenu.Item onClick={() => setShowInviteActivity(true)}>
                    <FontAwesomeIcon icon={faClockRotateLeft}/> View invite activity
                  </DropdownMenu.Item>
                ) : null}
              </DropdownMenu.Content>
            </DropdownMenu>
          </div>
          <div>
            <EditContactLink lead={lead}/>
          </div>
        </div>
        <Card className="mt-4">
          <Row className="align-items-stretch">
            <Col className="border-end" lg={3} md={4}>
              <Card.Body>
                <LeadInfo lead={lead}/>
                <EditContactLink lead={lead}/>
              </Card.Body>
            </Col>
            <Col>
              <Card.Body>
                {newLeadDetails()}
              </Card.Body>
            </Col>
          </Row>
        </Card>

        {/* Keyed on the email because the modal seeds its form from `email` only on
            mount; without a remount, "Try again" after "Edit email address" would
            resend to the old (bounced) address. */}
        <ShareLinkByEmailModal
          description={`${SHARE_DOC_MODAL_DESCRIPTION}${useVaultGates ? WITH_GATES : WITHOUT_GATES}`}
          email={lead.leadData.email}
          fromEmails={project.brokersForShareDocs}
          hideModal={hideShareByEmailModal}
          key={lead.leadData.email}
          leadId={lead.id}
          propertyId={project.id}
          show={showShareByEmailModal}
          title="Invite to View documents"
        />

        {showInviteActivityUi ? (
          <InviteActivityModal
            lead={lead}
            show={showInviteActivity}
            onEditEmail={() => {
              setShowInviteActivity(false);
              showUpdateLeadModal(lead);
            }}
            onHide={() => setShowInviteActivity(false)}
            onRetry={() => {
              // Reopens the send modal rather than firing a silent resend, so the
              // broker can correct the address before trying again.
              setShowInviteActivity(false);
              openShareByEmailModal();
            }}
          />
        ) : null}
      </div>
    </React.Fragment>
  );
}

export function CopyInviteLinkMenuItem({ label, url }) {
  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(url);
    showSnackbar({ text: 'Link has been copied to your clipboard', variant: 'success' });
  };

  return (
    <DropdownMenu.Item onClick={copyLinkToClipboard}>
      <FontAwesomeIcon icon={faCopy}/> {label}
    </DropdownMenu.Item>
  );
}

CopyInviteLinkMenuItem.propTypes = {
  label: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired
};

LeadShow.propTypes = {
  features: PropTypes.shape({
    clientReport: PropTypes.bool.isRequired,
    leadInviteActivity: PropTypes.bool,
    projectTasksActivities: PropTypes.bool.isRequired
  }),
  fetchLeads: PropTypes.func.isRequired,
  isFetchingLeads: PropTypes.bool,
  lead: PropTypes.object,
  match: ReactRouterPropTypes.match.isRequired,
  project: PropTypes.object.isRequired,
  refreshLead: PropTypes.func.isRequired,
  removeLead: PropTypes.func.isRequired,
  showUpdateLeadModal: PropTypes.func.isRequired,
  useVaultGates: PropTypes.bool.isRequired
};

const actions = {
  removeLead,
  refreshLead,
  fetchLeads,
  showUpdateLeadModal
};

const mapStateToProps = (state, ownProps) => {
  const leadId = parseInt(ownProps.match.params['id']);

  const isLeadsEmpty = Object.keys(state.leads).length === 0;
  const fetchingState = state.leads.fetching;

  // leads are fetched on mount in bundles/project/components/leads/list, which hasnt mounted yet
  // need to check if leads are being fetched in the scenario of navigating directly to a lead by URL
  // or refreshing the leads show page
  const isFetchingLeads = fetchingState === true || (fetchingState === undefined && isLeadsEmpty);
  const lead = isFetchingLeads ? null : state.leads.find(lead => lead.id === leadId);

  return {
    features: state.features,
    isFetchingLeads: isFetchingLeads,
    lead: lead,
    project: getProject(state),
    useVaultGates: state.project.useVaultGates
  };
};

export default withRouter(connect(mapStateToProps, actions)(LeadShow));
